/**
 * GlobalRedAlert - Shows a persistent blinking red button when new leads arrive
 * Appears in the sidebar/header and persists until clicked
 */
import React, { useState, useEffect, useCallback } from 'react';
import { AlertTriangle, X, Phone, Mail, User } from 'lucide-react';
import useWebSocket from '../hooks/useWebSocket';

const GlobalRedAlert = ({ userId = 'global-alerts' }) => {
  const [alerts, setAlerts] = useState([]);
  const [showPanel, setShowPanel] = useState(false);

  // Handle new red alert
  const handleRedAlert = useCallback((payload) => {
    console.log('[GlobalRedAlert] New alert:', payload);
    
    // Play alert sound
    try {
      const audio = new Audio('/alert-sound.mp3');
      audio.volume = 0.5;
      audio.play().catch(() => {});
    } catch (e) {
      // Ignore audio errors
    }

    // Add to alerts list
    setAlerts(prev => [{
      id: Date.now(),
      contact: payload.contact,
      timestamp: new Date(),
      source: payload.contact?.source || 'unknown',
      auto_response: payload.auto_response_type,
    }, ...prev.slice(0, 9)]); // Keep last 10 alerts
  }, []);

  // Connect to WebSocket
  const { isConnected } = useWebSocket({
    userId,
    onRedAlert: handleRedAlert,
  });

  // Clear a specific alert
  const dismissAlert = (alertId) => {
    setAlerts(prev => prev.filter(a => a.id !== alertId));
  };

  // Clear all alerts
  const clearAllAlerts = () => {
    setAlerts([]);
    setShowPanel(false);
  };

  // Navigate to lead
  const viewLead = (contactId) => {
    window.location.href = `/leads?contact=${contactId}`;
  };

  if (alerts.length === 0) return null;

  return (
    <>
      {/* Blinking Red Button - Always visible when alerts exist */}
      <button
        onClick={() => setShowPanel(!showPanel)}
        className="red-alert-button"
        data-testid="global-red-alert-button"
      >
        <AlertTriangle className="w-5 h-5 animate-pulse" />
        <span className="font-bold">{alerts.length} NEW LEAD{alerts.length > 1 ? 'S' : ''}</span>
        <span className="red-alert-ping" />
      </button>

      {/* Alert Panel */}
      {showPanel && (
        <div className="red-alert-panel" data-testid="red-alert-panel">
          <div className="flex items-center justify-between p-3 border-b border-red-800 bg-red-900/50">
            <h3 className="text-white font-bold flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-400" />
              New Lead Alerts
            </h3>
            <div className="flex items-center gap-2">
              <button
                onClick={clearAllAlerts}
                className="text-xs text-red-300 hover:text-white px-2 py-1 rounded bg-red-800/50 hover:bg-red-700"
              >
                Clear All
              </button>
              <button
                onClick={() => setShowPanel(false)}
                className="text-red-300 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
          
          <div className="max-h-96 overflow-y-auto">
            {alerts.map(alert => (
              <div
                key={alert.id}
                className="p-3 border-b border-red-800/50 hover:bg-red-900/30 cursor-pointer"
                onClick={() => viewLead(alert.contact?.contact_id)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 text-white font-medium">
                      <User className="w-4 h-4 text-red-400" />
                      {alert.contact?.first_name} {alert.contact?.last_name}
                    </div>
                    <div className="text-sm text-red-200 mt-1 space-y-1">
                      {alert.contact?.phone && (
                        <div className="flex items-center gap-2">
                          <Phone className="w-3 h-3" />
                          {alert.contact.phone}
                        </div>
                      )}
                      {alert.contact?.email && (
                        <div className="flex items-center gap-2">
                          <Mail className="w-3 h-3" />
                          {alert.contact.email}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-2 text-xs">
                      <span className="px-2 py-0.5 rounded bg-red-800/50 text-red-200 capitalize">
                        {alert.source?.replace(/_/g, ' ')}
                      </span>
                      {alert.auto_response && (
                        <span className="px-2 py-0.5 rounded bg-green-800/50 text-green-200">
                          Auto-{alert.auto_response} sent
                        </span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      dismissAlert(alert.id);
                    }}
                    className="text-red-400 hover:text-white ml-2"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <div className="text-xs text-red-300 mt-2">
                  {new Date(alert.timestamp).toLocaleTimeString()}
                </div>
              </div>
            ))}
          </div>
          
          <div className="p-3 border-t border-red-800 bg-red-900/30">
            <a
              href="/leads"
              className="block text-center text-red-200 hover:text-white text-sm"
            >
              View All Leads →
            </a>
          </div>
        </div>
      )}

      {/* CSS Styles */}
      <style>{`
        .red-alert-button {
          position: fixed;
          top: 16px;
          right: 16px;
          z-index: 9999;
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px 20px;
          background: linear-gradient(135deg, #dc2626 0%, #991b1b 100%);
          border: 2px solid #fca5a5;
          border-radius: 8px;
          color: white;
          font-size: 14px;
          cursor: pointer;
          animation: pulse-glow 1s ease-in-out infinite;
          box-shadow: 0 0 20px rgba(220, 38, 38, 0.5), 0 0 40px rgba(220, 38, 38, 0.3);
        }

        .red-alert-button:hover {
          transform: scale(1.05);
          box-shadow: 0 0 30px rgba(220, 38, 38, 0.7), 0 0 60px rgba(220, 38, 38, 0.4);
        }

        .red-alert-ping {
          position: absolute;
          top: -4px;
          right: -4px;
          width: 16px;
          height: 16px;
          background: #fef08a;
          border-radius: 50%;
          animation: ping 1s cubic-bezier(0, 0, 0.2, 1) infinite;
        }

        @keyframes pulse-glow {
          0%, 100% {
            box-shadow: 0 0 20px rgba(220, 38, 38, 0.5), 0 0 40px rgba(220, 38, 38, 0.3);
          }
          50% {
            box-shadow: 0 0 30px rgba(220, 38, 38, 0.8), 0 0 60px rgba(220, 38, 38, 0.5);
          }
        }

        @keyframes ping {
          75%, 100% {
            transform: scale(2);
            opacity: 0;
          }
        }

        .red-alert-panel {
          position: fixed;
          top: 70px;
          right: 16px;
          z-index: 9998;
          width: 360px;
          max-width: calc(100vw - 32px);
          background: linear-gradient(180deg, #450a0a 0%, #1c0606 100%);
          border: 2px solid #dc2626;
          border-radius: 12px;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.5), 0 0 20px rgba(220, 38, 38, 0.3);
          overflow: hidden;
        }
      `}</style>
    </>
  );
};

export default GlobalRedAlert;
